import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  AbstractAppConfigService,
  APP_CONFIG_SERVICE,
  IHttpBasicResponse,
  JsonApiResponse,
} from '@boxx/core';
import { IBrandigApiProps } from '@boxx/dynamic-branding/lib/services/dynamic-branding-service';
import { EcwidModel } from '@boxx/store-core/lib/models/Ecwid.model';
import { Observable } from 'rxjs';
import { ConfigNotificationsDataProps } from '../models/BusinessConfig.model';
import {
  BusinessGetApiRespProps,
  BusinessSiteFields,
  BusinessUpdateApiRespProps,
  BusinessUpdatePayloadProps,
  ICreateUserProps,
  IDeleteImageFormProps,
  IDeleteImageResponse,
  IGetBusinessImagesApiRespoProps,
  IGetSiteImagesProps,
  ISearchClientResponse,
  IWizardCreateUserApiRespProps,
  IWizardCreateUserPayloadProps,
  IWizardUserBusinessAdditionalInfoResponse,
  IWizardUserBusinessInfoResponse,
  IWizardUserPersonalInfoResponse,
  WizardGetTemplatesApiRespProps,
} from './IOnboarding.api';
import {
  IBusinessUpdateInfoPayload,
  IImageFormProps,
  IOnboardingRepository,
  IRatingAppProps,
  ITokenEcwid,
  UpdateMetaPayloadProps,
} from './IOnboarding.repository';

@Injectable()
export class OnboardingRepository implements IOnboardingRepository {
  constructor(
    @Inject(APP_CONFIG_SERVICE)
    private appSettings: AbstractAppConfigService,
    private httpClient: HttpClient
  ) {}

  getBusinessData() {
    return this.httpClient.get<JsonApiResponse<BusinessGetApiRespProps>>(
      `${this.getBaseUrl()}/business/get`
    );
  }

  getTemplates(query: string) {
    
    return this.httpClient.post<
      JsonApiResponse<WizardGetTemplatesApiRespProps>
    >(
      `${this.getBaseUrl()}/wizard/get_templates`,
      this.buildHttpParams({ query })
    );
  }

  getSiteFields() {
    return this.httpClient.get<JsonApiResponse<BusinessSiteFields>>(
      `${this.getBaseUrl()}/site/get_language`
    );
  }

  getSiteFieldsByLanguage(lng: string) {
    return this.httpClient.get<JsonApiResponse<BusinessSiteFields>>(
      `${this.getBaseUrl()}/site/get_language?language_code=${lng}`
    );
  }

  getSiteImages() {
    return this.httpClient.get<JsonApiResponse<IGetSiteImagesProps>>(
      `${this.appSettings.baseUrl()}/api/json/v1/business/get_images/`,
    );
  }

  createDirectory() {
    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/wizard/create_all`,
      null
    );
  }

  getAssociations() {
    return this.httpClient.get<JsonApiResponse<any>>(
      `${this.getBaseUrlV2()}/wizard/get_associations`
    );
  }

  createHostedPage(payload: any) {
    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.getBaseUrl()}/chargebee/createHostedPage`,
      this.buildHttpParams(payload)
    );
  }

  createHostedPageShipping(payload: any) {
    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.getBaseUrl()}/chargebee/createHostedPageShipping/${payload.brand_id}`,
      this.buildHttpParams(payload)
    );
  }

  validatePayment(brand_id: number, id: string) {
    return this.httpClient.get<JsonApiResponse<any>>(
      `${this.getBaseUrl()}/chargebee/validateHostedPayment/${brand_id}/${id}`
    );
  }

  validateUser(payload: any) {
    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.getBaseUrlV2()}/wizard/validate_user`,
      this.buildHttpParams(payload)
    );
  }

  getItemPrices(brandId: number) {
    return this.httpClient.get<JsonApiResponse<any>>(
      `${this.getBaseUrl()}/chargebee/getItemPrices/${brandId}`
    );
  }

  updateSiteFields(payload: BusinessSiteFields): Observable<JsonApiResponse<any>> {
    const urlSearchParams = new URLSearchParams();

    Object.keys(payload).forEach((key: string) => {
      urlSearchParams.append(key, payload[key]);
    });

    const body = urlSearchParams.toString();

    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/site/update_language`,
      body
    );
  }

  updateSiteFieldsEnglish(payload: any): Observable<JsonApiResponse<any>> {
    const urlSearchParams = new URLSearchParams();

    Object.keys(payload).forEach((key: string) => {
      urlSearchParams.append(key, payload[key]);
    });

    const body = urlSearchParams.toString();

    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/site/update_language?language_code=en`,
      body
    );
  }

  getImagesData(): Observable<
    JsonApiResponse<IGetBusinessImagesApiRespoProps>
  > {
    throw new Error('Method not implemented.');
  }

  deleteBusinesImage(payload: IDeleteImageFormProps) {
    let params = new HttpParams();
    for (const key in payload) {
      if (payload.hasOwnProperty(key)) {
        params = params.append(key, payload[key]);
      }
    }
    const body = params.toString();

    return this.httpClient.post<JsonApiResponse<IDeleteImageResponse>>(
      `${this.appSettings.baseUrl()}/api/json/v1/business/delete_image`,
      body
    );
  }

  uploadImageData(payload: IImageFormProps): Observable<JsonApiResponse<any>> {
    const urlSearchParams = new URLSearchParams();

    Object.keys(payload).forEach((key: string) => {
      urlSearchParams.append(key, payload[key]);
    });

    const body = urlSearchParams.toString();

    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/business/update_image`,
      body
    );
  }

  getUploadImagesUrl() {
    return this.getBaseUrl() + '/wizard/upload_image';
  }

  getColorThemes(): any {
    return this.httpClient.get<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/wizard/get_color_themes`,
    );
  }

  verifySubscriptionStatus(brand_id: any, subscription_id: any): any {
    return this.httpClient.get<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/chargebee/verifySubscriptionStatus/${brand_id}/${subscription_id}`,
    );
  }

  getPanelLink(): any {
    return this.httpClient.get<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/ecwid/sso`,
    );
  }

  // -------------- REGISTRATION -----------------
  saveUserData(payload: IWizardCreateUserPayloadProps) {
    return this.httpClient.post<JsonApiResponse<IWizardCreateUserApiRespProps>>(
      `${this.getBaseUrl()}/wizard/create_user`,
      this.buildHttpParams(payload)
    );
  }
  
  saveUserFormDataMeed(payload: FormData) {
    let _options = { headers: new HttpHeaders({ 
      'enctype': 'multipart/form-data; charset=UTF-8;', 'X-Requested-With': 'XMLHttpRequest' 
    })};

    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v2/wizard/create_user`,
      payload,
      _options
    );
  }
 
  getConfigNotifications() {
    let _options = { headers: new HttpHeaders({ 
      'enctype': 'multipart/form-data; charset=UTF-8;', 'X-Requested-With': 'XMLHttpRequest' 
    })};

    return this.httpClient.get<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v2/notifications/get_settings`,
      _options
    );
  }

  updateConfigNotifications(payload: FormData) {
    let _options = { headers: new HttpHeaders({ 
      'enctype': 'multipart/form-data; charset=UTF-8;', 'X-Requested-With': 'XMLHttpRequest' 
    })};

    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v2/notifications/update_settings`,
      payload,
      _options
    );
  }

  validaZipcode(zipcode: number): Observable<any> {
    return this.httpClient.get<any>(
      `${this.appSettings.baseUrl()}/api/json/v1/wizard/search_zipcode/${zipcode}`,
    );
  }

  // ------------ BUSINESS CONFIGURATION ----------

  updateBusinessData(payload: BusinessUpdatePayloadProps) {
    return this.httpClient.post<JsonApiResponse<BusinessUpdateApiRespProps>>(
      `${this.getBaseUrl()}/business/update`,
      this.buildHttpParams(payload)
    );
  }

  updateNotificationsData(payload: any) {
    const urlSearchParams = new URLSearchParams();

    Object.keys(payload).forEach((key: string) => {
      urlSearchParams.append(key, payload[key]);
    });

    const body = urlSearchParams.toString();

    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/notifications/update_settings`,
      body
    );
  }

  getNotificationsSettings(): Observable<ConfigNotificationsDataProps> {
    return this.httpClient.get<ConfigNotificationsDataProps>(
      `${this.appSettings.baseUrl()}/api/json/v1/notifications/get_settings`,
    );
  }

  createSchedulingLink(): Observable<any> {
    return this.httpClient.get<any>(
      `${this.appSettings.baseUrl()}/api/json/v1/calendly/createSchedulingLink`,
    );
  }

  validateSchedulingLink(): Observable<any> {
    return this.httpClient.get<any>(
      `${this.appSettings.baseUrl()}/api/json/v1/calendly/validateSchedulingLink`,
    );
  }

  updateBusinessInfoData(payload: IBusinessUpdateInfoPayload) {
    return this.httpClient.post<JsonApiResponse<BusinessUpdateApiRespProps>>(
      `${this.getBaseUrl()}/business/update_info`,
      this.buildHttpParams(payload)
    );
  }

  createWebsite() {
    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.getBaseUrl()}/wizard/create_website`,
      null
    );
  }

  updateMeta(
    payload: UpdateMetaPayloadProps
  ): Promise<JsonApiResponse<BusinessUpdateApiRespProps>> {
    const keyName = Object.keys(payload)[0];
    const data = { key: keyName, value: payload[keyName] };

    return this.httpClient
      .post<JsonApiResponse<BusinessUpdateApiRespProps>>(
        `${this.getBaseUrl()}/business/update_meta`,
        this.buildHttpParams(data)
      )
      .toPromise();
  }

  //---- save App Rating-------
  saveScoreData(payload: IRatingAppProps) {
    return this.httpClient.post<JsonApiResponse<BusinessUpdateApiRespProps>>(
      `${this.getBaseUrl()}/calificacion/create`,
      this.buildHttpParams(payload)
    );
  }

  //---- Save Token Ecwid-------
  setTokenEcwid( payload: ITokenEcwid
  ): Observable<IHttpBasicResponse<EcwidModel>> {
    return this.httpClient.get<IHttpBasicResponse<EcwidModel>>(
      `${this.getBaseUrl()}/Oauthwid/update/${payload.bId}/${payload.businessId}`
    );
  }

  //---- business utils-------
  getWizardUserPersonalInfo(
    businessId: number
  ): Observable<IWizardUserPersonalInfoResponse> {
    return this.httpClient.get<IWizardUserPersonalInfoResponse>(
      `${this.appSettings.baseUrl()}/businesses/boxx-crm/wizard/sitebuilder_personal_data?businessId=${businessId}`
    );
  }
  getWizardBusinessInfo(
    businessId: number
  ): Observable<IWizardUserBusinessInfoResponse> {
    return this.httpClient.get<IWizardUserBusinessInfoResponse>(
      `${this.appSettings.baseUrl()}/businesses/boxx-crm/wizard/business_info?businessId=${businessId}`
    );
  }
  getWizardBusinessAdditionalInfo(
    businessId: number
  ): Observable<IWizardUserBusinessAdditionalInfoResponse> {
    return this.httpClient.get<IWizardUserBusinessAdditionalInfoResponse>(
      `${this.appSettings.baseUrl()}/businesses/boxx-crm/wizard/business_products?businessId=${businessId}`
    );
  }
  searchClient(
    email: string
  ): Observable<JsonApiResponse<ISearchClientResponse>> {
    const payload = this.buildHttpParams({ email });
    return this.httpClient.post<
      JsonApiResponse<ISearchClientResponse | undefined>
    >(`${this.getBaseUrl()}/wizard/search_client`, payload);
  }

  //--- Location  ---
  // getSearchByZipcode(zipcode: number): Observable<any> {
  //   return this.httpClient.get<any>(
  //     `${this.appSettings.baseUrl()}/api/json/v1/wizard/search_zipcode/${zipcode}`,
  //   );
  // }

  getSearchByZipcode(zipcode: number): Observable<any> {
    return this.httpClient.get<any>(
      `${this.appSettings.baseUrl()}/api/json/v1/wizard/search_zipcode/${zipcode}`,
    );
  }

  getStates(): Observable<any> {
    return this.httpClient.get<any>(
      `${this.appSettings.baseUrl()}/api/json/v1/wizard/get_states`,
    );
  }

  getLocalities(state: number): Observable<any> {
    return this.httpClient.get<any>(
      `${this.appSettings.baseUrl()}/api/json/v1/wizard/get_localities/${state}`,
    );
  }

  getSettlements(locality: number): Observable<any> {
    return this.httpClient.get<any>(
      `${this.appSettings.baseUrl()}/api/json/v1/wizard/get_settlements/${locality}`,
    );
  }

  //--- BOOKING----
  getBookingServices(): Observable<any> {
    return this.httpClient.get<any>(
      `${this.appSettings.baseUrl()}/api/json/v1/booking/api/services`,
    );
  }

  //--- class utils----

  private buildHttpParams(payload: any) {
    let params = new HttpParams();

    for (const key in payload) {
      if (payload.hasOwnProperty(key)) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        typeof payload[key] === 'object'
          ? (params = params.append(key, JSON.stringify(payload[key])))
          : (params = params.append(key, payload[key]));
      }
    }
    return params;
  }

  validationCode(code: string): Observable<JsonApiResponse<IBrandigApiProps>> {
    return this.httpClient.get<JsonApiResponse<IBrandigApiProps>>(
      `${this.appSettings.baseUrl()}/api/json/v1/theme/confirm_brand/${code}`
    );
  }

  sendResetCode(payload: any) {
    const urlSearchParams = new URLSearchParams();

    Object.keys(payload).forEach((key: string) => {
      urlSearchParams.append(key, payload[key]);
    });

    const body = urlSearchParams.toString();

    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/auth/send_reset_password_code`,
      body
    );
  }

  validateCode(payload: any) {
    const urlSearchParams = new URLSearchParams();

    Object.keys(payload).forEach((key: string) => {
      urlSearchParams.append(key, payload[key]);
    });

    const body = urlSearchParams.toString();

    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/auth/validate_reset_password_code`,
      body
    );
  }

  updatePassword(payload: any) {
    const urlSearchParams = new URLSearchParams();

    Object.keys(payload).forEach((key: string) => {
      urlSearchParams.append(key, payload[key]);
    });

    const body = urlSearchParams.toString();

    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/json/v1/auth/reset_password`,
      body
    );
  }

  private getBaseUrl() {
    return `${this.appSettings.baseUrl()}/api/json/v1`;
  }

  private getBaseUrlV2() {
    return `${this.appSettings.baseUrl()}/api/json/v2`;
  }

  private getBaseCleanUrlV2() {
    return `${this.appSettings.baseUrl()}/api/v2`;
  }

  //Redesign
  registerUser(payload: ICreateUserProps) {
    return this.httpClient.post<JsonApiResponse<IWizardCreateUserApiRespProps>>(
      `${this.getBaseUrlV2()}/wizard/create_user`,
      this.buildHttpParams(payload)
    );
  }

  validateEmail(payload: any) {
    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.appSettings.baseUrl()}/api/v2/auth/check_user`,
      this.buildHttpParams(payload)
    );
  }

  registerTelmex(payload: any) {
    return this.httpClient.post<JsonApiResponse<any>>(
      `${this.getBaseUrl()}/telmex/register_user`,
      this.buildHttpParams(payload)
    );
  }

  getLawDocuments(brandId: number, language: string, type: string) {
    return this.httpClient.get<JsonApiResponse<BusinessSiteFields>>(
      `${this.getBaseCleanUrlV2()}/auth/legal_documents?brand_id=${brandId}&language=${language}&type=${type}`
    );
  }
}